import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { bankingAPI } from '../../api';
import { formatDateForInput } from '../../lib/utils';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Textarea } from '../../components/ui/Textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';

const BankingAccountEditor = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    account_name: '',
    account_type: 'current',
    bank_name: '',
    account_number: '',
    sort_code: '',
    opening_balance: '0.00',
    opening_balance_date: new Date().toISOString().split('T')[0],
    credit_limit: '',
    notes: '',
  });

  const [isSystemAccount, setIsSystemAccount] = useState(false);

  useEffect(() => {
    if (isEditMode) {
      fetchAccount();
    }
  }, [id]);

  const fetchAccount = async () => {
    try {
      setLoading(true);
      setError('');
      const account = await bankingAPI.getAccount(id);

      setFormData({
        account_name: account.account_name || '',
        account_type: account.account_type || 'current',
        bank_name: account.bank_name || '',
        account_number: account.account_number || '',
        sort_code: account.sort_code || '',
        opening_balance: parseFloat(account.opening_balance || 0).toFixed(2),
        opening_balance_date: formatDateForInput(account.opening_balance_date) || new Date().toISOString().split('T')[0],
        credit_limit: account.credit_limit ? parseFloat(account.credit_limit).toFixed(2) : '',
        notes: account.notes || '',
      });

      setIsSystemAccount(account.is_system_account || false);
    } catch (err) {
      setError(err.message || 'Failed to load account');
      console.error('Error fetching account:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.account_name.trim()) {
      setError('Account name is required');
      return;
    }

    try {
      setSaving(true);
      setError('');

      const payload = {
        ...formData,
        opening_balance: parseFloat(formData.opening_balance) || 0,
        credit_limit: formData.credit_limit ? parseFloat(formData.credit_limit) : null,
      };

      if (isEditMode) {
        await bankingAPI.updateAccount(id, payload);
      } else {
        await bankingAPI.createAccount(payload);
      }

      navigate('/tenant/banking');
    } catch (err) {
      setError(err.message || 'Failed to save account');
      console.error('Error saving account:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(amount || 0);
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card>
          <CardHeader>
            <CardTitle className="text-display text-[var(--ds-text-primary)]">
              {isEditMode ? 'Edit Bank Account' : 'New Bank Account'}
            </CardTitle>
          </CardHeader>

          {loading ? (
            <CardContent className="p-8 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--ds-accent-primary)]"></div>
              <p className="mt-2 text-xs text-[var(--ds-text-muted)]">Loading account...</p>
            </CardContent>
          ) : (
            <form onSubmit={handleSubmit}>
              <CardContent className="p-6 space-y-6">
              {error && (
                <div className="bg-[var(--ds-status-danger-bg)] border border-[var(--ds-status-danger)]/30 rounded-[var(--ds-radius-md)] px-4 py-3">
                  <p className="text-xs text-[var(--ds-status-danger)]">{error}</p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Account Name */}
                <div className="md:col-span-2">
                  <Input
                    label="Account Name"
                    type="text"
                    name="account_name"
                    value={formData.account_name}
                    onChange={handleChange}
                    disabled={isSystemAccount}
                    required
                    placeholder="e.g., Main Business Account"
                    requiredIndicator
                    className="h-8"
                  />
                  {isSystemAccount && (
                    <p className="mt-1 text-xs text-[var(--ds-text-muted)]">System accounts cannot be renamed</p>
                  )}
                </div>

                {/* Account Type */}
                <div>
                  <Select
                    label="Account Type"
                    name="account_type"
                    value={formData.account_type}
                    onChange={handleChange}
                    disabled={isSystemAccount}
                    required
                    requiredIndicator
                    className="h-8"
                  >
                    <option value="current">Current Account</option>
                    <option value="savings">Savings Account</option>
                    <option value="petty_cash">Petty Cash</option>
                    <option value="proprietor_drawings">Proprietor Drawings</option>
                    <option value="credit_card">Credit Card</option>
                    <option value="payment_gateway">Payment Gateway</option>
                    <option value="loan_account">Loan Account</option>
                    <option value="other">Other</option>
                  </Select>
                </div>

                {/* Bank Name */}
                <div>
                  <Input
                    label="Bank Name"
                    type="text"
                    name="bank_name"
                    value={formData.bank_name}
                    onChange={handleChange}
                    placeholder="e.g., Barclays"
                    className="h-8"
                  />
                </div>

                {/* Sort Code */}
                <div>
                  <Input
                    label="Sort Code (UK format)"
                    type="text"
                    name="sort_code"
                    value={formData.sort_code}
                    onChange={handleChange}
                    placeholder="XX-XX-XX"
                    pattern="[0-9]{2}-[0-9]{2}-[0-9]{2}"
                    className="h-8"
                  />
                  <p className="mt-1 text-xs text-[var(--ds-text-muted)]">Format: XX-XX-XX</p>
                </div>

                {/* Account Number */}
                <div>
                  <Input
                    label="Account Number"
                    type="text"
                    name="account_number"
                    value={formData.account_number}
                    onChange={handleChange}
                    placeholder="8 digits"
                    className="h-8"
                  />
                </div>

                {/* Opening Balance */}
                <div>
                  <Input
                    label="Opening Balance"
                    type="number"
                    name="opening_balance"
                    value={formData.opening_balance}
                    onChange={handleChange}
                    step="0.01"
                    placeholder="0.00"
                    className="h-8"
                  />
                  <p className="mt-1 text-xs text-[var(--ds-text-muted)]">Use negative values for loans, overdrafts, etc.</p>
                </div>

                {/* Opening Balance Date */}
                <div>
                  <Input
                    label="Opening Balance Date"
                    type="date"
                    name="opening_balance_date"
                    value={formatDateForInput(formData.opening_balance_date)}
                    onChange={handleChange}
                    className="h-8"
                  />
                </div>

                {/* Credit Limit - Only show for credit_card and loan_account */}
                {(formData.account_type === 'credit_card' || formData.account_type === 'loan_account') && (
                  <div>
                    <Input
                      label="Credit Limit"
                      type="number"
                      name="credit_limit"
                      value={formData.credit_limit}
                      onChange={handleChange}
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      className="h-8"
                    />
                    <p className="mt-1 text-xs text-[var(--ds-text-muted)]">Maximum credit available for this account</p>
                  </div>
                )}

                {/* Notes */}
                <div className="md:col-span-2">
                  <Textarea
                    label="Notes"
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    rows={3}
                    placeholder="Additional notes about this account..."
                    className="text-xs"
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 pt-6 border-t border-[var(--ds-border-divider)]">
                <Button
                  variant="outline"
                  size="sm"
                  type="button"
                  onClick={() => navigate('/tenant/banking')}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  type="submit"
                  disabled={saving}
                >
                  {saving ? 'Saving...' : isEditMode ? 'Update Account' : 'Create Account'}
                </Button>
              </div>
              </CardContent>
            </form>
          )}
        </Card>
      </div>
    </Layout>
  );
};

export default BankingAccountEditor;

